import { createContext, useContext, useState, useRef, useEffect } from 'react';
import axios from 'axios';

const AudioContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extended list of popular reciters from AlQuran Cloud API
const POPULAR_RECITERS = [
  { id: 'ar.alafasy', reciter_name: 'Mishary Rashid Alafasy', api_type: 'alquran' },
  { id: 'ar.abdulbasitmurattal', reciter_name: 'Abdul Basit (Murattal)', api_type: 'alquran' },
  { id: 'ar.abdulsamad', reciter_name: 'Abdul Basit (Mujawwad)', api_type: 'alquran' },
  { id: 'ar.husary', reciter_name: 'Mahmoud Khalil Al-Hussary', api_type: 'alquran' },
  { id: 'ar.shaatree', reciter_name: 'Abu Bakr al-Shatri', api_type: 'alquran' },
  { id: 'ar.minshawi', reciter_name: 'Mohamed Siddiq al-Minshawi', api_type: 'alquran' },
  { id: 'ar.husarymujawwad', reciter_name: 'Mahmoud Khalil Al-Hussary (Mujawwad)', api_type: 'alquran' },
  { id: 'ar.mahermuaiqly', reciter_name: 'Maher Al Muaiqly', api_type: 'alquran' },
  { id: 'ar.muhammadayyub', reciter_name: 'Muhammad Ayyub', api_type: 'alquran' },
  { id: 'ar.muhammadjibreel', reciter_name: 'Muhammad Jibreel', api_type: 'alquran' },
  { id: 'ar.saoodshuraym', reciter_name: 'Saood bin Ibraaheem Ash-Shuraym', api_type: 'alquran' },
  { id: 'ar.abdullaahawwaad', reciter_name: 'Abdullah Awwaad Al-Juhani', api_type: 'alquran' },
  { id: 'ar.hanirifai', reciter_name: 'Hani Ar-Rifai', api_type: 'alquran' },
  { id: 'ar.abdurrahmaansudais', reciter_name: 'Abdurrahmaan As-Sudais', api_type: 'alquran' },
  { id: 'ar.abdullaahbasfar', reciter_name: 'Abdullah Basfar', api_type: 'alquran' },
  { id: 'ar.banderbalilah', reciter_name: 'Bander Baleelah', api_type: 'alquran' },
  { id: 'ar.khaalidal-qahtaanee', reciter_name: 'Khalid Al-Qahtani', api_type: 'alquran' },
  { id: 'ar.ahmedajamy', reciter_name: 'Ahmed ibn Ali al-Ajamy', api_type: 'alquran' },
  { id: 'ar.aymanswoaid', reciter_name: 'Ayman Sowaid', api_type: 'alquran' },
  { id: 'ar.abdullahmatroud', reciter_name: 'Abdullah Matroud', api_type: 'alquran' },
  { id: 'ar.yasser-ad-dussary', reciter_name: 'Yasser Ad-Dussary', api_type: 'alquran' },
  { id: 'ar.mahmoudalisabr', reciter_name: 'Mahmoud Ali Al-Banna', api_type: 'alquran' },
  { id: 'ar.saudalshuraim', reciter_name: 'Saud Al-Shuraim', api_type: 'alquran' },
  { id: 'ar.nasser_alqatami', reciter_name: 'Nasser Al Qatami', api_type: 'alquran' },
];

export const AudioProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentVerse, setCurrentVerse] = useState(null);
  const [currentReciter, setCurrentReciter] = useState(POPULAR_RECITERS[0]);
  const [reciters, setReciters] = useState(POPULAR_RECITERS);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [continuousPlay, setContinuousPlay] = useState(true);
  const [surahVerses, setSurahVerses] = useState([]);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  const audioRef = useRef(new Audio());
  
  // Use refs to store latest values for event listeners
  const surahVersesRef = useRef([]);
  const currentVerseIndexRef = useRef(0);
  const continuousPlayRef = useRef(true);

  // Keep refs in sync with state
  useEffect(() => {
    surahVersesRef.current = surahVerses;
  }, [surahVerses]);

  useEffect(() => {
    currentVerseIndexRef.current = currentVerseIndex;
  }, [currentVerseIndex]);

  useEffect(() => {
    continuousPlayRef.current = continuousPlay;
  }, [continuousPlay]);

  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setProgress(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      console.log('Audio ended, continuous:', continuousPlayRef.current, 'verses:', surahVersesRef.current.length, 'index:', currentVerseIndexRef.current);
      
      // Use refs to get latest values
      if (continuousPlayRef.current && surahVersesRef.current.length > 0) {
        const nextIndex = currentVerseIndexRef.current + 1;
        console.log('Next index:', nextIndex, 'Total verses:', surahVersesRef.current.length);
        
        if (nextIndex < surahVersesRef.current.length) {
          console.log('Playing next verse:', surahVersesRef.current[nextIndex]);
          setCurrentVerseIndex(nextIndex);
          // Use a small delay to ensure state is updated
          setTimeout(() => {
            playVerseInternal(surahVersesRef.current[nextIndex]);
          }, 100);
        } else {
          console.log('Reached end of surah');
        }
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []); // Empty dependency array - we use refs for latest values

  const playVerseInternal = async (verse) => {
    try {
      console.log('Playing verse:', verse);
      // Fetch audio URL from backend
      const response = await axios.get(`${API}/verse-audio/${verse.verseKey}?reciter=${currentReciter.id}`);
      const audioUrl = response.data.audio_url;

      audioRef.current.src = audioUrl;
      await audioRef.current.play();
      setCurrentVerse(verse);
      setIsPlaying(true);
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
    }
  };

  const playVerse = async (verse) => {
    // Calculate which verse in the surah list
    if (surahVerses.length > 0) {
      const index = surahVerses.findIndex(v => v.verse_key === verse.verseKey);
      if (index !== -1) {
        setCurrentVerseIndex(index);
      }
    }
    await playVerseInternal(verse);
  };

  const playFullSurah = (chapterId, verses) => {
    console.log('Starting full surah playback with', verses.length, 'verses');
    const verseList = verses.map(v => ({
      chapterId,
      verseNumber: v.verse_number,
      verseKey: v.verse_key
    }));
    
    setSurahVerses(verseList);
    setCurrentVerseIndex(0);
    setContinuousPlay(true);
    
    // Play first verse
    if (verseList.length > 0) {
      playVerseInternal(verseList[0]);
    }
  };

  const togglePlayPause = async () => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (error) {
        console.error('Error playing audio:', error);
      }
    }
  };

  const playNext = () => {
    if (surahVerses.length > 0 && currentVerseIndex < surahVerses.length - 1) {
      const nextIndex = currentVerseIndex + 1;
      setCurrentVerseIndex(nextIndex);
      playVerse(surahVerses[nextIndex]);
    }
  };

  const playPrevious = () => {
    if (surahVerses.length > 0 && currentVerseIndex > 0) {
      const prevIndex = currentVerseIndex - 1;
      setCurrentVerseIndex(prevIndex);
      playVerse(surahVerses[prevIndex]);
    }
  };

  const changeReciter = (reciterId) => {
    const reciter = reciters.find((r) => r.id === reciterId);
    if (reciter) {
      setCurrentReciter(reciter);
      // Replay current verse with new reciter if there's a current verse
      if (currentVerse) {
        playVerse(currentVerse);
      }
    }
  };

  const toggleContinuousPlay = () => {
    setContinuousPlay(!continuousPlay);
  };

  const seek = (time) => {
    audioRef.current.currentTime = time;
    setProgress(time);
  };

  return (
    <AudioContext.Provider
      value={{
        isPlaying,
        currentVerse,
        currentReciter,
        reciters,
        progress,
        duration,
        continuousPlay,
        playVerse,
        playFullSurah,
        togglePlayPause,
        playNext,
        playPrevious,
        changeReciter,
        toggleContinuousPlay,
        seek
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};